import React, { useState, useEffect } from "react";
import "./RemoveLiquidity.css";
import Loading from "../../Loading/Loading";
import { toast } from "react-toastify";
import { formatUnits } from "viem";
import {
  removeLiquidity,
  getPosition,
  validateUserOwnsPosition,
  getPoolAddress,
} from "../../../helpers/v3/v3.js";
import { publicClient, walletClient } from "../../../Contracts/V3/client.js";
import { ADDRS } from "../../../Contracts/V3/addresses.js";
import { ethers, formatEther, toBigInt } from "ethers";
import * as API from "../../../Utils/Services/api";
import { ERC20 } from "../../../Contracts/V3/abis.js";
import { useLanguage } from "../../LanguageContext.jsx";
import translation from "../../Translations/translation.json";

function pretty(addr) {
  if (!addr) return "";
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

const RemoveLiquidityV3 = () => {
  const [positionId, setPositionId] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [positionData, setPositionData] = useState(null);
  const [fee, setfee] = useState("");
  const [accountAvailable, setAccountAvailable] = useState(
    sessionStorage.getItem("account")
  );
  const [removePercent, setRemovePercent] = useState("0");
  const [slippageBps, setSlippageBps] = useState(50);

  const [meta0, setMeta0] = useState({ symbol: "", decimals: 18 });
  const [meta1, setMeta1] = useState({ symbol: "", decimals: 18 });

  const { language } = useLanguage();
  const translate = (key) => {
    const keys = key.split(".");
    return (
      keys.reduce((obj, keyPart) => obj?.[keyPart], translation[language]) ||
      key
    );
  };

  const removeBps = Math.round(
    Math.max(0, Math.min(100, Number(removePercent))) * 100
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setAccountAvailable(sessionStorage.getItem("account"));
    }, 500);
    return () => clearInterval(interval);
  }, [accountAvailable]);

  const displaySymbol = (symbol) => (symbol === "WETH" ? "ETHW" : symbol);

  const loadPosition = async () => {
    if (!positionId) return;
    if (!accountAvailable)
      return toast.warn(translate("banner.connectmetamask"));
    setFetching(true);
    setPositionData(null);
    setMeta0({ symbol: "", decimals: 18 });
    setMeta1({ symbol: "", decimals: 18 });

    try {
      const id = toBigInt(positionId);

      const [caller] = await walletClient.requestAddresses();
      const res = await validateUserOwnsPosition({
        tokenId: positionId,
        caller,
        posmAddress: ADDRS.POSITION_MANAGER,
        publicClient,
      });

      if (!res.ok) {
        const reasons = {
          emptyTokenId: translate("remove.emptyTokenId"),
          noCaller: translate("banner.connectmetamask"),
          invalidTokenId: translate("remove.invalidTokenId"),
          notOwner: translate("remove.notOwner"),
          noLiquidity: translate("remove.noLiquidity"),
        };
        toast.error(reasons[res.reason]);
        return;
      }

      const pos = await getPosition(id);
      setfee(pos.fee);

      const [sym0, dec0] = await Promise.all([
        publicClient
          .readContract({
            address: pos.token0,
            abi: ERC20,
            functionName: "symbol",
          })
          .catch(() => pretty(pos.token0)),
        publicClient
          .readContract({
            address: pos.token0,
            abi: ERC20,
            functionName: "decimals",
          })
          .catch(() => 18),
      ]);

      const [sym1, dec1] = await Promise.all([
        publicClient
          .readContract({
            address: pos.token1,
            abi: ERC20,
            functionName: "symbol",
          })
          .catch(() => pretty(pos.token1)),
        publicClient
          .readContract({
            address: pos.token1,
            abi: ERC20,
            functionName: "decimals",
          })
          .catch(() => 18),
      ]);
      setMeta0({
        symbol: displaySymbol(typeof sym0 === "string" ? sym0 : pos.token0),
        decimals: Number(dec0) || 18,
      });

      setMeta1({
        symbol: displaySymbol(typeof sym1 === "string" ? sym1 : pos.token1),
        decimals: Number(dec1) || 18,
      });

      setPositionData({
        token0: pos.token0,
        token1: pos.token1,
        feeTier: `${Number(pos.fee) / 10000}%`,
        liquidity: pos.liquidity,
        tokensOwed0: pos.tokensOwed0,
        tokensOwed1: pos.tokensOwed1,
      });
    } catch (e) {
      console.log("Load Position", e);
      toast.success(translate("remove.notOwner"));
    } finally {
      setFetching(false);
    }
  };

  const removeHandler = async () => {
    if (removePercent === "0") return;
    setLoading(true);
    const pool = await getPoolAddress(
      positionData.token0,
      positionData.token1,
      fee
    );

    try {
      const r = await removeLiquidity({
        tokenId: toBigInt(positionId),
        removeBps,
        slippageBps: Math.max(0, Math.min(5000, Number(slippageBps) || 0)),
      });

      const payload = {
        wallet: accountAvailable,
        lpTokenId: Number(positionId),
        removePercent: Number(removePercent),
        sqrtPriceX96: Number(r.sqrtPriceX96),
        tokenBalance: parseFloat(formatEther(r.tokenBalance)),
        ethwReserve: parseFloat(formatEther(r.ethwReserve)),
        tokenReserve: parseFloat(formatEther(r.tokenReserve)),
        createdBlock: Number(r.decRc.blockNumber),
        createdTx: r.decRc.transactionHash,
      };
      const resp = await API.removeLiquidityV3(payload, pool);

      toast.success(translate("remove.removeLiqSucc"));
      setRemovePercent("0");
      setPositionData(null);
    } catch (e) {
      toast.error(e?.shortMessage);
      console.log("Remove Liquidity", e);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setPositionData(null);
    setRemovePercent("0");
  };

  return (
    <div>
      {!positionData ? (
        <div className="migrate-pg-body">
          <p>{translate("remove.tokenId")}</p>
          <div className="add-postions-form-btn selected-token-btn">
            <input
              type="text"
              className="form-control search-input-box search-input-box-liqui pt-2 pb-2"
              value={positionId}
              placeholder={translate("remove.enterTokenId")}
              onChange={(e) => setPositionId(e.target.value)}
              autoComplete="off"
            />
          </div>

          <div className="get-started-btn mt-4 remove-btn">
            <button
              className="text-white"
              onClick={loadPosition}
              disabled={loading || fetching || !positionId}
            >
              {fetching ? <Loading /> : translate("createLiq.continue")}
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="rl-card">
            <div className="rl-header">
              <div className="rl-pair">
                <strong>
                  {meta0.symbol} / {meta1.symbol}
                </strong>
                <span className="rl-fee-tag">{positionData.feeTier}</span>
              </div>
            </div>

            <div className="rl-liquidity-info">
              <div className="rl-amt">
                <span>{translate("remove.liquidity")}</span>
                <strong>{ethers.formatUnits(positionData.liquidity)}</strong>
              </div>
            </div>

            {/* <div className="rl-range mt-2">
              {ethers.formatUnits(positionData.tokensOwed0)} {meta0.symbol} /{" "}
              {ethers.formatUnits(positionData.tokensOwed1)} {meta1.symbol}
            </div> */}
          </div>

          <div className="rl-slider">
            <div className="rl-slider-heading">
              <div>{translate("remove.removepercent")}</div>
            </div>
            <div className="rl-percent-buttons">
              {[10, 25, 50, 75, 100].map((percent) => (
                <button
                  key={percent}
                  className={`rl-percent-btn ${
                    Number(removePercent) === percent ? "active" : ""
                  }`}
                  onClick={() => setRemovePercent(percent.toString())}
                >
                  {percent}%
                </button>
              ))}
            </div>
          </div>

          <div className="mt-3">
            <div className="form-label">{translate("remove.slippage")}</div>
            <input
              type="text"
              value={slippageBps}
              onChange={(e) => setSlippageBps(e.target.value)}
              className="form-control search-input-box search-input-box-liqui pt-2 pb-2"
              placeholder="50"
            />
            <div className="rl-amt mt-1">
              <span> {translate("remove.slippageInfo")}</span>
            </div>
          </div>

          <div className="rl-actions">
            <button className="rl-back-btn" onClick={handleBack}>
              {translate("createLiq.back")}
            </button>
            <button
              className="rl-btn remove"
              onClick={removeHandler}
              disabled={loading || removePercent === "0"}
            >
              {loading ? <Loading /> : translate("remove.removeLiquidity")}
            </button>
          </div>
        </>
      )}
    </div>
  );
};
export default RemoveLiquidityV3;
